import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { devicesAPI, staffToken } from "../../services/api";
import { useI18n } from "../../i18n";
import LanguageSelect from "../../components/LanguageSelect";
import PinPad from "./PinPad";
import NewPinForm from "../../components/NewPinForm";

/**
 * The till stays locked until a cashier says who they are.
 *
 * The device token already proves this register is licensed; the PIN answers
 * "who is serving", so every sale is attributed to someone. Four digits, no
 * username, no password — and no way past it.
 *
 * Rendered into document.body rather than inside the register. The header
 * uses backdrop-blur, and a filtered ancestor becomes the containing block for
 * position: fixed: that is how the old PIN pad ended up laid out against a
 * 56px header and pushed half off the top of the screen.
 *
 * The language can be changed from here, before anyone has unlocked: whoever
 * picks up the till should be able to read it.
 */
const TillLock = ({ storeName, onUnlocked }) => {
  const { t } = useI18n();
  // Someone who unlocked with the shipped default PIN has to replace it first
  const [choosing, setChoosing] = useState(null);

  const unlock = async (pin) => {
    try {
      const { data } = await devicesAPI.unlock(pin);

      if (data.staff.must_change_pin) {
        setChoosing(data);
        return null;
      }

      staffToken.set(data.staff_token, data.staff);
      onUnlocked(data.staff);
      return null;
    } catch (err) {
      return err.response?.status === 401
        ? t("pos.lock.notRecognised")
        : t("pos.lock.checkFailed");
    }
  };

  const saveNewPin = async (pin) => {
    try {
      await devicesAPI.changePin(pin, choosing.staff_token);
      staffToken.set(choosing.staff_token, { ...choosing.staff, must_change_pin: false });
      onUnlocked({ ...choosing.staff, must_change_pin: false });
      return null;
    } catch (err) {
      return err.response?.data?.message || t("auth.credentials.pinFailed");
    }
  };

  // The register behind must not scroll under the lock
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t("pos.lock.title")}
      className="fixed inset-0 z-[70] bg-slate-950 text-slate-100 overflow-y-auto overscroll-contain"
    >
      <div className="absolute top-4 end-4">
        <LanguageSelect tone="dark" />
      </div>

      <div className="min-h-full flex flex-col items-center justify-center px-6 py-16 pb-[calc(2.5rem+env(safe-area-inset-bottom))]">
        <span className="text-4xl leading-none" aria-hidden="true">
          💨
        </span>
        <p className="mt-3 text-lg font-bold text-white text-center">
          {storeName || t("pos.lock.registerFallback")}
        </p>
        <p className="mt-1 text-sm text-slate-400 text-center">
          {t("pos.lock.prompt")}
        </p>

        {choosing ? (
          <>
            <p className="mt-6 text-lg font-semibold text-white text-center">
              {t("auth.credentials.pinTitle")}
            </p>
            <NewPinForm onSave={saveNewPin} />
          </>
        ) : (
          <>
            <PinPad onSubmit={unlock} />

            <p className="mt-8 max-w-xs text-center text-xs text-slate-500">
              {t("pos.lock.noPin")}
            </p>
            <Link
              to="/admin/staff"
              className="mt-2 text-sm font-semibold text-primary-400 hover:text-primary-300"
            >
              {t("pos.lock.manage")}
            </Link>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default TillLock;
